import { ApiError, RequestStatus } from 'features/common'
import { useMemo, useState } from 'react'
import { shallowEqual } from 'react-redux'
import { useAppDispatch } from '.'
import { useAppSelector } from './hooks'
import { AppDispatch, RootState } from './store'

export function createReduxActionDefinition<T>(
  fun: (dispatch: AppDispatch, arg: T) => void,
  props: {
    status: (state: RootState, arg: T) => RequestStatus
    error: (state: RootState, arg: T) => ApiError | null | undefined
    dismissResult?: (dispatch: AppDispatch, arg: T) => void
  }
) {
  return {
    fun,
    ...props
  }
}

export interface ReduxAction<T> {
  invoke(arg: T): void
  dismissResult(): void
  status: RequestStatus
  error: ApiError | null | undefined
}

export function useReduxAction<T>(definition: {
  fun: (dispatch: AppDispatch, arg: T) => void
  status: (state: RootState, arg: T) => RequestStatus
  error: (state: RootState, arg: T) => ApiError | null | undefined
  dismissResult?: (dispatch: AppDispatch, arg: T) => void
}): ReduxAction<T> {
  const dispatch = useAppDispatch()
  const [invocationState, setInvokedState] = useState<{ invoked: false } | { invoked: true; arg: T }>({
    invoked: false
  })
  const { status, error } = useAppSelector(
    (state) => ({
      status: invocationState.invoked ? definition.status(state, invocationState.arg) : RequestStatus.Idle,
      error: invocationState.invoked ? definition.error(state, invocationState.arg) : undefined
    }),
    shallowEqual
  )

  return useMemo(
    () => ({
      invoke(arg: T) {
        setInvokedState({ invoked: true, arg })
        definition.fun(dispatch, arg)
      },
      dismissResult() {
        if (!invocationState.invoked) {
          return
        }
        if (definition.dismissResult) {
          definition.dismissResult(dispatch, invocationState.arg)
        }
        setInvokedState({ invoked: false })
      },
      status,
      error
    }),
    [invocationState, status, error, definition, dispatch]
  )
}
